import { useCallback } from 'react';
import monitoring from '../common/libs/monitoring';
import getConfig from '../common/config';
import Firebase, { signInWithCustomToken } from '../common/libs/firebase';

/**
 * IDトークン期限切れを示すID基盤の詳細エラーコード
 */
const ID_TOKEN_EXPIRED_ERROR_CODE = 'E40140019';

/**
 * リフレッシュ対象外のAPIパス（無限ループ防止）
 */
const REFRESH_EXCLUDED_PATHS = ['logout', 'auth/token/refresh'];

/**
 * リフレッシュ結果（新しいIDトークンとID基盤ログインのレスポンス）
 */
interface RefreshResult {
  newIdToken: string;
  loginRes: Response;
}

/**
 * 並行リフレッシュ制御用（モジュールスコープ）
 * 複数の401が同時発生しても、リフレッシュは1回のみ実行される
 */
let refreshPromise: Promise<RefreshResult | null> | null = null;

/**
 * レスポンスがトークンリフレッシュ対象かを判定する
 * - ステータス 401
 * - 詳細エラーコードに E40140019 を含む
 */
async function isTokenExpiredResponse(res: Response): Promise<boolean> {
  if (res.status !== 401) return false;

  try {
    const body = await res.clone().json();
    const details: Array<{ id?: string }> =
      body?.resultInfo?.messageInfo?.details ?? [];
    const isTokenExpired = details.some(
      (d) => d.id === ID_TOKEN_EXPIRED_ERROR_CODE,
    );
    return isTokenExpired;
  } catch {
    return false;
  }
}

/**
 * リフレッシュ対象外パスかどうかを判定する
 */
function isRefreshExcludedPath(input: RequestInfo, idBaseUrl: string): boolean {
  const url = typeof input === 'string' ? input : input.url;
  const isExcluded = REFRESH_EXCLUDED_PATHS.some(
    (path) => url === `${idBaseUrl}/api/${path}` || url === path,
  );
  return isExcluded;
}

/**
 * ログインAPIのパスかどうかを判定する
 */
function isLoginApiPath(input: RequestInfo, idBaseUrl: string): boolean {
  const url = typeof input === 'string' ? input : input.url;
  return url === `${idBaseUrl}/api/login` || url === 'login';
}

/**
 * ID基盤のリフレッシュAPI呼び出し → Firebase再認証 → ID基盤ログインによるCookie更新を行い、
 * 新しいIDトークンとログインレスポンスを返す
 */
async function refreshTokens(idBaseUrl: string): Promise<RefreshResult | null> {
  try {
    // 1. ID基盤のリフレッシュAPI（rt-Cookie は credentials: 'include' で自動送信）
    const refreshRes = await fetch(`${idBaseUrl}/api/auth/token/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!refreshRes.ok) {
      console.error(`Token refresh failed: ${refreshRes.status}`);
      return null;
    }

    const { customToken } = await refreshRes.json();

    // 2. カスタムトークンでFirebaseに再ログイン
    const auth = Firebase.getAuth();
    const userCredential = await signInWithCustomToken(auth, customToken);

    // 3. 新しいidTokenを取得（onIdTokenChanged の反映を待たず直接取得）
    const newIdToken = await userCredential.user.getIdToken();

    // 4. ID基盤にログインして認証済みCookieを更新
    const loginRes = await fetch(`${idBaseUrl}/api/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${newIdToken}`,
      },
    });

    if (!loginRes.ok) {
      console.error(`ID login after refresh failed: ${loginRes.status}`);
      return null;
    }

    return { newIdToken, loginRes };
  } catch (e) {
    console.error('Token refresh error:', e);
    return null;
  }
}

export default function useIdApiClient() {
  const {
    publicRuntimeConfig: { idBaseUrl },
  } = getConfig();

  /**
   * ID基盤のAPIラッパー
   */
  const fetchWrapper = async (
    input: RequestInfo,
    init?: RequestInit,
    skipRefresh = false,
    token?: string,
  ): Promise<Response> => {
    let editedInput: RequestInfo;
    if (typeof input === 'string') {
      editedInput = `${idBaseUrl}/api/${input}`;
    } else {
      editedInput = input;
    }

    const additionalHeaders: Record<string, string> = {};
    const editedToken = token ?? (await Firebase.getIdToken());
    if (editedToken) {
      additionalHeaders['Authorization'] = `Bearer ${editedToken}`;
    }
    if (init?.method) {
      additionalHeaders['Content-Type'] = 'application/json';
    }
    const options: RequestInit = {
      ...init,
      credentials: 'include',
      headers: {
        ...init?.headers,
        ...additionalHeaders,
      },
    };

    const res = await fetch(editedInput, options);

    // 401 + E40140019（IDトークン期限切れ）の場合、トークンリフレッシュを試行
    if (
      !skipRefresh &&
      !isRefreshExcludedPath(input, idBaseUrl) &&
      (await isTokenExpiredResponse(res))
    ) {
      // 並行リフレッシュ制御：進行中のリフレッシュがあれば待つ
      if (!refreshPromise) {
        refreshPromise = refreshTokens(idBaseUrl).finally(() => {
          refreshPromise = null;
        });
      }

      const refreshResult = await refreshPromise;

      // リフレッシュ成功時は新しいトークンで元のリクエストをリトライ
      // ログインAPIはrefreshTokens内で呼び出し済みのため、そのレスポンスを返す（二重呼び出し防止）
      if (refreshResult) {
        if (!isLoginApiPath(input, idBaseUrl)) {
          return fetchWrapper(input, init, true, refreshResult.newIdToken);
        }
        // Responseのbodyは1度しか読み取れないため、clone()で複製して返す
        return refreshResult.loginRes.clone();
      }
      // リフレッシュ失敗 → 元の401レスポンスをそのまま返す
    }

    if (500 <= res.status && res.status <= 599) {
      const notifyMessage = await res
        .clone()
        .json()
        .then((data) => {
          return JSON.stringify(data?.error);
        })
        .catch(() => {
          return res.statusText;
        });
      monitoring.notify({
        name: `API ERROR DETECTED (${res.status})`,
        message: notifyMessage,
      });
    }

    return res;
  };

  const idApiClient = useCallback(fetchWrapper, [idBaseUrl]);

  interface ProceedIdPageOptions {
    skipLoginRedirect?: boolean;
    replace?: boolean;
  }
  /**
   * ID基盤のWebページへ遷移
   *
   * @param path リダイレクト先Path
   * @param discloseId Antaaアカウント外部ユーザーID
   * @param skipLoginRedirect 認証失敗時にログイン画面への遷移をスキップするか
   * @param replace 画面遷移をreplaceで行うかどうか
   */
  const proceedIdPage = useCallback(
    async (
      path: string,
      discloseId?: string | null,
      options: ProceedIdPageOptions = {},
    ) => {
      const { skipLoginRedirect = false, replace = false } = options;

      // ID基盤ログイン（認証済みCookieを更新するため必ず呼ぶ）
      let option: RequestInit = {
        method: 'POST',
      };
      if (discloseId) {
        option.body = JSON.stringify({ discloseId });
      }

      const res = await idApiClient('login', option);
      if (!skipLoginRedirect && res.status === 401) {
        console.error(`ID Login error; status = ${res.status}`);
        if (discloseId) {
          // QA／Antaaアカウントありの場合で認証エラー検出時は再認証へリダイレクト
          window.location.href = `/logout?title=認証期限が切れました。再度ログインしてください。&redirectUrl=${encodeURIComponent(
            idBaseUrl + path,
          )}`;
          return;
        }
      } else if (res.status !== 200) {
        // 認証エラー以外のエラーは遷移先画面で表示するため無視する
        console.error(`ID Login error; status = ${res.status}`);
      }

      // ID基盤の画面へ遷移
      if (replace) {
        window.location.replace(`${idBaseUrl}${path}`);
      } else {
        window.location.href = `${idBaseUrl}${path}`;
      }
    },
    [idApiClient, idBaseUrl],
  );

  return { idApiClient, proceedIdPage };
}
